import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  createDraftApplication,
  uploadResume,
  getResumePrefillStatus,
  uploadVideo,
  updateCandidateProfileByApplicationId,
  submitApplication,
  UpdateCandidateProfilePayload,
} from '../../candidate/api/application.api';


interface CreateDraftPayload {
  jobId: string;
  source?: string;
  referralToken?: string;
  referralUtm?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
  };
}


interface UploadResumePayload {
  draftId: string;
  file: File;
}


interface ResumePrefillStatusPayload {
  draftId: string;
  resumeId?: string;
  extractionVersion?: number;
}


interface UploadVideoPayload {
  draftId: string;
  file: File;
}


interface SubmitApplicationPayload {
  draftId: string;
}


export const createDraftApplicationThunk = createAsyncThunk(
  'application/createDraft',
  async (
    payload: CreateDraftPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await createDraftApplication(payload);

      return response;
    } catch (error: any) {
      const status = error?.response?.status;

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.details?.message ||
        error?.message;

      if (
        status === 409 ||
        (
          typeof message === 'string' &&
          (
            message.toLowerCase().includes('already applied') ||
            message
              .toLowerCase()
              .includes('cannot apply for another job')
          )
        )
      ) {
        return rejectWithValue(
          'You have already applied for a job. You cannot apply for another job. Please check your application status.'
        );
      }

      return rejectWithValue(
        typeof message === 'string' && message.trim()
          ? message
          : 'Failed to create draft application.'
      );
    }
  }
);


export const uploadResumeThunk = createAsyncThunk(
  'application/uploadResume',
  async (
    payload: UploadResumePayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await uploadResume(payload);

      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        'Resume upload failed.';

      return rejectWithValue(message);
    }
  }
);


export const getResumePrefillStatusThunk = createAsyncThunk(
  'application/getResumePrefillStatus',
  async (
    payload: ResumePrefillStatusPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await getResumePrefillStatus(
        payload.draftId
      );

      return {
        ...response,
        _resumeId: payload.resumeId,
        _extractionVersion: payload.extractionVersion,
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        'Failed to fetch resume prefill status.';

      return rejectWithValue(message);
    }
  }
);


export const updateCandidateProfileThunk = createAsyncThunk(
  'application/updateCandidateProfile',
  async (
    payload: UpdateCandidateProfilePayload,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await updateCandidateProfileByApplicationId(payload);

      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        'Failed to update candidate profile.';

      return rejectWithValue(message);
    }
  }
);


export const uploadVideoThunk = createAsyncThunk(
  'application/uploadVideo',
  async (
    payload: UploadVideoPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await uploadVideo(payload);

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "We couldn't upload your video. Please try again."
      );
    }
  }
);


export const submitApplicationThunk = createAsyncThunk(
  'application/submitApplication',
  async (
    payload: SubmitApplicationPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await submitApplication(
        payload.draftId
      );

      return response;
    } catch (error: any) {
      const status = error?.response?.status;

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.details?.message ||
        error?.message;

      if (
        status === 409 ||
        (
          typeof message === 'string' &&
          (
            message.toLowerCase().includes('already applied') ||
            message
              .toLowerCase()
              .includes('cannot apply for another job')
          )
        )
      ) {
        return rejectWithValue(
          'You have already applied for a job. You cannot apply for another job. Please check your application status.'
        );
      }

      return rejectWithValue(
        typeof message === 'string' && message.trim()
          ? message
          : 'Failed to submit application.'
      );
    }
  }
);